import React, { useRef, useEffect, useState, useCallback } from 'react';

function drawSpottingResult(ctx, texts, polys, scale, hoveredIdx) {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);

    for (let i = 0; i < polys.length; i++) {
        const poly = polys[i];
        if (!poly || poly.length < 4) continue;
        const text = (texts[i] || '').trim();
        if (!text) continue;

        const isHovered = i === hoveredIdx;
        const pts = poly.map(p => [p[0] * scale, p[1] * scale]);

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let j = 1; j < pts.length; j++) ctx.lineTo(pts[j][0], pts[j][1]);
        ctx.closePath();

        if (isHovered) {
            ctx.fillStyle = 'rgba(255, 156, 40, 0.12)';
            ctx.fill();
            ctx.strokeStyle = '#4658ff';
            ctx.lineWidth = 1.8;
            ctx.stroke();
        }
        ctx.restore();

        const dx = pts[1][0] - pts[0][0];
        const dy = pts[1][1] - pts[0][1];
        const angle = Math.atan2(dy, dx);

        const minY = Math.min(...pts.map(p => p[1]));
        const maxY = Math.max(...pts.map(p => p[1]));
        const boxH = maxY - minY;

        const centerX = pts.reduce((s, p) => s + p[0], 0) / pts.length;
        const centerY = pts.reduce((s, p) => s + p[1], 0) / pts.length;

        let fontSize = Math.max(8, boxH * 0.8);
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(angle);
        ctx.font = `${fontSize}px "PingFang SC", "Helvetica Neue", -apple-system, sans-serif`;
        ctx.fillStyle = isHovered ? '#4658ff' : '#140e35';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';

        const edgeLen = Math.sqrt(dx * dx + dy * dy);
        const measured = ctx.measureText(text).width;
        if (measured > edgeLen * 0.95 && edgeLen > 0) {
            fontSize = Math.max(8, fontSize * edgeLen * 0.9 / measured);
            ctx.font = `${fontSize}px "PingFang SC", "Helvetica Neue", -apple-system, sans-serif`;
        }

        ctx.fillText(text, 0, 0);
        ctx.restore();
    }
}

export default function OcrSpottingView({ pageData, pageIndex, hoveredIdx: externalHoveredIdx, onHover }) {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [localHoveredIdx, setLocalHoveredIdx] = useState(null);

    const hoveredIdx = externalHoveredIdx ?? localHoveredIdx;
    const setHoveredIdx = (idx) => { setLocalHoveredIdx(idx); onHover?.(idx); };

    const spottingRes = pageData?.spottingRes;
    const texts = spottingRes?.rec_texts || [];
    const polys = spottingRes?.rec_polys || [];
    const pw = pageData?.pageWidth || 800;
    const ph = pageData?.pageHeight || 600;

    const dpr = window.devicePixelRatio || 1;
    const scale = containerWidth > 0 ? containerWidth / pw : 1;
    const canvasW = Math.round(pw * scale);
    const canvasH = Math.round(ph * scale);

    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!canvasRef.current || !containerWidth) return;
        canvasRef.current.width = canvasW * dpr;
        canvasRef.current.height = canvasH * dpr;
        const ctx = canvasRef.current.getContext('2d');
        ctx.scale(dpr, dpr);
        drawSpottingResult(canvasRef.current.getContext('2d'), texts, polys, scale, hoveredIdx);
    }, [texts, polys, scale, hoveredIdx, canvasW, canvasH, containerWidth]);

    const hitTest = useCallback((e) => {
        if (!canvasRef.current) return -1;
        const rect = canvasRef.current.getBoundingClientRect();
        const mx = (e.clientX - rect.left) / scale;
        const my = (e.clientY - rect.top) / scale;
        for (let i = polys.length - 1; i >= 0; i--) {
            const p = polys[i];
            if (!p || p.length < 4) continue;
            const minX = Math.min(...p.map(pt => pt[0]));
            const maxX = Math.max(...p.map(pt => pt[0]));
            const minY = Math.min(...p.map(pt => pt[1]));
            const maxY = Math.max(...p.map(pt => pt[1]));
            if (mx >= minX && mx <= maxX && my >= minY && my <= maxY) return i;
        }
        return -1;
    }, [polys, scale]);

    if (!spottingRes) return null;

    return (
        <div ref={containerRef} style={{ width: '100%' }}>
            <canvas
                ref={canvasRef}
                width={canvasW}
                height={canvasH}
                style={{ display: 'block', width: '100%', height: 'auto', cursor: 'pointer' }}
                onMouseMove={e => {
                    const idx = hitTest(e);
                    setHoveredIdx(idx >= 0 ? idx : null);
                }}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={e => {
                    const idx = hitTest(e);
                    if (idx >= 0 && texts[idx]) navigator.clipboard.writeText(texts[idx]);
                }}
            />
            <div className="flex items-center gap-3 px-4 py-1">
                <div className="flex-1 h-px" style={{ background: '#e1e6f8' }} />
                <span style={{ fontSize: '10px', color: '#aeb4c9' }}>Page {pageIndex + 1}</span>
                <div className="flex-1 h-px" style={{ background: '#e1e6f8' }} />
            </div>
        </div>
    );
}
